import React, { useCallback, useEffect, useState } from 'react';
import {
  Award, RefreshCw, Search, FileText, CheckCircle, XCircle,
  ClipboardList, ChevronLeft, ChevronRight,
} from 'lucide-react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { adminService } from '../services/adminService';
import { KycSubmission } from '../types';
import { formatCompactNumber, formatNumber, formatPercent } from '../utils/format';
import './AdminPage.css';
import './Compliance.css';

const CHART_COLORS = ['#22c55e', '#f59e0b', '#ef4444', '#410C6D', '#5a1494'];

// ─── KYC Queue ───────────────────────────────────────────────────────────────

interface ApproveModalProps {
  sub: KycSubmission;
  onClose: () => void;
  onDone: () => void;
}

const ApproveModal: React.FC<ApproveModalProps> = ({ sub, onClose, onDone }) => {
  const [tier, setTier] = useState(sub.tier_requested || 'tier_1');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setLoading(true);
    setError('');
    try {
      await adminService.approveKycSubmission(sub.id, { tier_granted: tier, note: note || undefined });
      onDone();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Approval failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <h2>Approve KYC — #{sub.id}</h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 16 }}>
          {sub.user_full_name || `User ${sub.user_id}`} · {sub.id_type?.toUpperCase()} · {sub.provider}
        </p>
        <label>Tier to grant</label>
        <select value={tier} onChange={(e) => setTier(e.target.value)}>
          <option value="tier_1">Tier 1 (Basic KYC)</option>
          <option value="tier_2">Tier 2 (Enhanced KYC)</option>
          <option value="tier_3">Tier 3 (Full KYC)</option>
        </select>
        <label>Internal note (optional)</label>
        <textarea
          rows={3}
          placeholder="Add a note for the audit trail…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        {error && <p style={{ color: '#dc2626', fontSize: '0.85rem', margin: '0 0 12px 0' }}>{error}</p>}
        <div className="modal-actions">
          <button className="btn secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn" onClick={submit} disabled={loading}>
            <CheckCircle size={14} />
            {loading ? 'Approving…' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
};

interface RejectModalProps {
  sub: KycSubmission;
  onClose: () => void;
  onDone: () => void;
}

const RejectModal: React.FC<RejectModalProps> = ({ sub, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!reason.trim()) { setError('Please provide a rejection reason.'); return; }
    setLoading(true);
    setError('');
    try {
      await adminService.rejectKycSubmission(sub.id, reason.trim());
      onDone();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Rejection failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <h2>Reject KYC — #{sub.id}</h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 16 }}>
          {sub.user_full_name || `User ${sub.user_id}`} · {sub.id_type?.toUpperCase()} · {sub.provider}
        </p>
        <label>Rejection reason <span style={{ color: '#dc2626' }}>*</span></label>
        <textarea
          rows={3}
          placeholder="e.g. ID image quality too low, name mismatch…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {error && <p style={{ color: '#dc2626', fontSize: '0.85rem', margin: '0 0 12px 0' }}>{error}</p>}
        <div className="modal-actions">
          <button className="btn secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn danger" onClick={submit} disabled={loading}>
            <XCircle size={14} />
            {loading ? 'Rejecting…' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PAGE_SIZE = 20;

const KycQueueTab: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [submissions, setSubmissions] = useState<KycSubmission[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [approveSub, setApproveSub] = useState<KycSubmission | null>(null);
  const [rejectSub, setRejectSub] = useState<KycSubmission | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.listKycSubmissions({
        status: statusFilter || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setSubmissions(res?.items ?? []);
      setTotal(res?.total ?? 0);
    } catch {
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const handleDone = () => {
    setApproveSub(null);
    setRejectSub(null);
    load();
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <>
      {approveSub && <ApproveModal sub={approveSub} onClose={() => setApproveSub(null)} onDone={handleDone} />}
      {rejectSub  && <RejectModal  sub={rejectSub}  onClose={() => setRejectSub(null)}  onDone={handleDone} />}

      <div className="kyc-toolbar">
        <div className="kyc-filter-row">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="">All statuses</option>
          </select>
          <span className="kyc-count">{total} submission{total !== 1 ? 's' : ''}</span>
        </div>
        <button className="btn secondary" onClick={() => load()} disabled={loading}>
          <RefreshCw size={13} className={loading ? 'spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="panel" style={{ marginBottom: 0 }}>
        <div className="panel-body tight">
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
          ) : submissions.length === 0 ? (
            <div className="empty">
              {statusFilter === 'pending'
                ? 'No pending KYC submissions — all clear!'
                : 'No submissions found for the selected filter.'}
            </div>
          ) : (
            <div className="kyc-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Provider</th>
                    <th>ID Type</th>
                    <th>Tier</th>
                    <th>Submitted</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((s) => (
                    <tr key={s.id}>
                      <td style={{ color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>{s.id}</td>
                      <td>
                        <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                          {s.user_full_name || `User ${s.user_id}`}
                        </div>
                        {s.user_email && (
                          <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>{s.user_email}</div>
                        )}
                      </td>
                      <td style={{ textTransform: 'capitalize' }}>{s.provider}</td>
                      <td>
                        <span className="pill">
                          {s.id_type?.toUpperCase() ?? '—'}
                        </span>
                      </td>
                      <td style={{ fontVariantNumeric: 'tabular-nums' }}>{s.tier_requested}</td>
                      <td>
                        <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                          {new Date(s.submitted_at).toLocaleDateString()}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          {new Date(s.submitted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </td>
                      <td>
                        <span className={`pill ${s.status}`}>{s.status}</span>
                        {s.rejection_reason && (
                          <div style={{ fontSize: '0.72rem', color: '#b91c1c', marginTop: 3, maxWidth: 160 }}>
                            {s.rejection_reason}
                          </div>
                        )}
                      </td>
                      <td>
                        {s.status === 'pending' && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              className="btn"
                              style={{ padding: '5px 10px', fontSize: '0.78rem' }}
                              onClick={() => setApproveSub(s)}
                            >
                              <CheckCircle size={12} /> Approve
                            </button>
                            <button
                              className="btn danger"
                              style={{ padding: '5px 10px', fontSize: '0.78rem' }}
                              onClick={() => setRejectSub(s)}
                            >
                              <XCircle size={12} /> Reject
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="kyc-pagination">
          <button
            className="btn secondary"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            <ChevronLeft size={14} /> Prev
          </button>
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            Page {page + 1} of {totalPages}
          </span>
          <button
            className="btn secondary"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      )}
    </>
  );
};

// ─── Skill Reports ────────────────────────────────────────────────────────────

const SkillReportsTab: React.FC = () => {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analytics, setAnalytics] = useState<any | null>(null);
  const [report, setReport] = useState<any | null>(null);
  const [userId, setUserId] = useState<string>('');
  const [userResult, setUserResult] = useState<any | null>(null);
  const [userError, setUserError] = useState<string>('');

  const load = useCallback(async () => {
    setRefreshing(true);
    const [a, r] = await Promise.allSettled([
      adminService.getComplianceAnalytics(days),
      adminService.getSkillBasedGamingReport(days),
    ]);
    if (a.status === 'fulfilled') setAnalytics(a.value);
    if (r.status === 'fulfilled') setReport(r.value);
    setLoading(false);
    setRefreshing(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const handleLookup = async () => {
    setUserError('');
    setUserResult(null);
    const id = parseInt(userId, 10);
    if (!Number.isFinite(id)) { setUserError('Enter a numeric user ID'); return; }
    try {
      const [reportRes, classRes] = await Promise.allSettled([
        adminService.getComplianceReport(id, days),
        adminService.getSkillClassification(id, days),
      ]);
      const merged: any = {};
      if (reportRes.status === 'fulfilled') merged.report = reportRes.value;
      if (classRes.status === 'fulfilled') merged.classification = classRes.value;
      setUserResult(merged);
    } catch (e: any) {
      setUserError(e?.response?.data?.detail || e?.message || 'Lookup failed');
    }
  };

  const skillDistribution = (() => {
    const dist =
      analytics?.skill_classification_distribution
      || analytics?.skill_distribution
      || analytics?.skill_based_classification?.distribution
      || {};
    return Object.entries(dist).map(([name, value]) => ({ name, value: Number(value) || 0 }));
  })();

  const quizAcc = (() => {
    const obj = analytics?.quiz_accuracy_by_source || analytics?.quiz_accuracy_by_category || {};
    return Object.entries(obj).map(([k, v]) => ({ name: k, accuracy: Number(v) || 0 }));
  })();

  return (
    <>
      <div className="skill-header-row">
        <div className="header-actions" style={{ marginBottom: 20 }}>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 365 days</option>
          </select>
          <button className="btn" onClick={load} disabled={refreshing}>
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi-card">
          <span className="kpi-label">Skill-classified users</span>
          <span className="kpi-value">
            {formatCompactNumber(
              analytics?.skill_classified_users
              ?? analytics?.skill_based_classification?.classified_users
              ?? 0
            )}
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">% Skill-based</span>
          <span className="kpi-value">
            {formatPercent(
              analytics?.skill_based_pct
              ?? analytics?.skill_classified_pct
              ?? analytics?.skill_based_classification?.skill_based_pct
              ?? 0,
              1
            )}
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Avg quiz accuracy</span>
          <span className="kpi-value">
            {(() => {
              const acc =
                analytics?.average_quiz_accuracy
                ?? analytics?.quiz_accuracy_avg
                ?? analytics?.skill_based_classification?.average_quiz_accuracy
                ?? 0;
              const v = Number(acc);
              return formatPercent(v <= 1 ? v * 100 : v, 1);
            })()}
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Reports generated</span>
          <span className="kpi-value">{formatNumber(report ? 1 : 0)}</span>
          <span className="kpi-detail">{days}d window</span>
        </div>
      </div>

      <div className="charts-row-2">
        <div className="panel">
          <div className="panel-header"><h3>Skill classification distribution</h3></div>
          <div className="panel-body">
            {loading ? <div className="skeleton" style={{ height: 240 }} /> : skillDistribution.length === 0 ? (
              <div className="empty">No classification data</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={skillDistribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                    {skillDistribution.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="panel">
          <div className="panel-header"><h3>Quiz accuracy</h3></div>
          <div className="panel-body">
            {loading ? <div className="skeleton" style={{ height: 240 }} /> : quizAcc.length === 0 ? (
              <div className="empty">No quiz accuracy breakdown</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={quizAcc}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
                  <Tooltip />
                  <Bar dataKey="accuracy" name="Accuracy" fill="#22c55e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <h3><Search size={16} /> Per-user compliance lookup</h3>
        </div>
        <div className="panel-body">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="number"
              placeholder="User ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                outline: 'none',
              }}
            />
            <button className="btn" onClick={handleLookup}><Search size={14} /> Lookup</button>
            {userError && <span style={{ fontSize: '0.85rem', color: '#dc2626' }}>{userError}</span>}
          </div>
          {userResult && (
            <pre className="code-pre">{JSON.stringify(userResult, null, 2)}</pre>
          )}
        </div>
      </div>

      {report && (
        <div className="panel">
          <div className="panel-header">
            <h3><FileText size={16} /> Skill-based gaming report</h3>
          </div>
          <div className="panel-body">
            <pre className="code-pre">{JSON.stringify(report, null, 2)}</pre>
          </div>
        </div>
      )}
    </>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

type Tab = 'kyc' | 'skill';

const Compliance: React.FC = () => {
  const [tab, setTab] = useState<Tab>('kyc');

  return (
    <div className="admin-page compliance-page">
      <div className="page-header">
        <div>
          <h1><Award size={20} style={{ marginRight: 8 }} />Compliance</h1>
          <p className="page-subtitle">KYC review queue and skill-based classification metrics</p>
        </div>
      </div>

      <div className="compliance-tabs">
        <button
          className={`compliance-tab${tab === 'kyc' ? ' active' : ''}`}
          onClick={() => setTab('kyc')}
        >
          <ClipboardList size={15} /> KYC Queue
        </button>
        <button
          className={`compliance-tab${tab === 'skill' ? ' active' : ''}`}
          onClick={() => setTab('skill')}
        >
          <Award size={15} /> Skill Reports
        </button>
      </div>

      {tab === 'kyc'   && <KycQueueTab />}
      {tab === 'skill' && <SkillReportsTab />}
    </div>
  );
};

export default Compliance;
